import { PlainMessage } from "@bufbuild/protobuf";
import { useContext } from "react";
import { CurrentUserContext } from "../context/RequireAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { ListTasksForAdvisorRequest } from "protogen/tasks_service_pb";
import {
  useCreateTask,
  useCreateMemberTask,
  useUpdateTask,
  useUpdateMemberTask,
} from "services/tasks";
import { CreateTaskRequest, EditTaskRequest } from "protogen/tasks_service_pb";
import { TaskState } from "protogen/tasks_pb";
import {
  ConfirmationState,
  useConfirmationDialog,
} from "../common/ConfirmationDialog";
import { EphemeralTask } from "../../types/tasks";
import { TaskFilter } from "components/tasks/FilterContext";
import { StageValue } from "./workflowStates";

type CreateTaskHelperReturn = {
  createTask: (task: EphemeralTask) => Promise<void>;
  loading: boolean;
};
const createTaskHelper = ({
  referral = "1",
  creatorType = "advisor",
  assigneeRef,
}: {
  referral?: string;
  creatorType?: "advisor" | "member";
  assigneeRef?: string;
}): CreateTaskHelperReturn => {
  const currentUser = useContext(CurrentUserContext);
  const navigate = useNavigate();
  const requestFn =
    creatorType === "advisor" ? useCreateTask : useCreateMemberTask;
  const { request, loading } = requestFn((r) => {
    if (creatorType === "advisor") {
      navigate(
        `/tasks/${encodeURIComponent(r?.task?.ref)}?newTaskFrom=${referral}`,
      );
    } else if (creatorType === "member") {
      navigate(`/tasks/${encodeURIComponent(r?.task?.ref)}`);
    }
  });

  const createTask = async (task: EphemeralTask) => {
    if (task.familyRef && task.title) {
      await request(
        new CreateTaskRequest({
          assigneeRef: assigneeRef ? assigneeRef : currentUser.ref,
          priority: "medium",
          ...task,
        }),
      );
    }
  };
  return { createTask, loading };
};

const useIsNewTask = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isFresh = !!queryParams.get("newTaskFrom");
  return isFresh;
};

const taskDeletion = (
  isMember: boolean = false,
): {
  deleteTask: (taskRef: string) => Promise<void>;
  confirmState: ConfirmationState;
} => {
  const isFresh = useIsNewTask();

  const navigate = useNavigate();
  // check if new.
  const confirmState = useConfirmationDialog();
  const useUpdateTaskFn = isMember ? useUpdateMemberTask : useUpdateTask;
  const { request } = useUpdateTaskFn();
  const deleteTask = async (taskRef: string) => {
    const resp = await request(
      new EditTaskRequest({
        taskRef,
        updatedState: TaskState.DELETED,
      }),
    );
    if (resp) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(`/tasks`);
      }
    }
  };
  const confirmDeleteTask = async (taskRef: string) => {
    confirmState.openDialog(async () => deleteTask(taskRef));
  };
  return {
    confirmState,
    deleteTask: isFresh ? deleteTask : confirmDeleteTask,
  };
};

const dateStringToDayAndDate = (dateString: string, shortDates: boolean) => {
  // "3/28/2024"
  let parts = dateString.split("/");
  const monthPart = parseInt(parts[0]) - 1; // months are 0-based in JavaScript
  const dayPart = parseInt(parts[1]);
  const yearPart = parseInt(parts[2]);

  // Passing in the parts makes the day explicit.  If not JS date will try to convert to users local tz
  const date = new Date(yearPart, monthPart, dayPart);
  const day = date.toLocaleDateString("en-US", {
    weekday: shortDates ? "short" : "long",
  });
  const dateStr = date.toLocaleDateString("en-US", {
    day: "numeric",
    ...(shortDates ? {} : { month: "short" }),
  });
  return [day, dateStr];
};

const minutesToDisplay = (minutes: number): string => {
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const notDoneStages = Object.values(StageValue).filter(
  (s) => s !== StageValue.Done && s !== StageValue.Deleted,
);

const hasNonDefaultFilters = (filters: TaskFilter[]): boolean => {
  return filters.some((f) => {
    if (f.field === "stage") {
      return (f.value as string[]).length > 0;
    }
    if (f.field === "status") return false;
    if (Array.isArray(f.value)) return f.value.length > 0;
    return !!f.value;
  });
};

const buildRequest = (
  filters: TaskFilter[],
): PlainMessage<ListTasksForAdvisorRequest> => {
  let resp: PlainMessage<ListTasksForAdvisorRequest> = {
    taskStates: [],
    familyRefs: [],
    categories: [],
    priorities: [],
    taskStages: [],
    sinceCreationTimeRange: "",
    includeUnread: !hasNonDefaultFilters(filters),
    projects: [],
  };
  filters.forEach((f) => {
    if (f.field === "status") {
      resp.taskStates = f.value as string[];
    } else if (f.field === "stage") {
      resp.taskStages = f.value as string[];
    } else if (f.field === "family") {
      resp.familyRefs = f.value as string[];
    } else if (f.field === "category") {
      resp.categories = f.value as string[];
    } else if (f.field === "priority") {
      resp.priorities = f.value as string[];
    } else if (f.field === "timeRange") {
      resp.sinceCreationTimeRange = f.value as string;
    } else if (f.field === "project") {
      resp.projects = f.value as string[];
    }
  });
  // if no stages are selected, show all not done stages
  if (resp.taskStages.length === 0) {
    resp.taskStages = notDoneStages;
  }
  return resp;
};

export {
  createTaskHelper,
  useIsNewTask,
  taskDeletion,
  dateStringToDayAndDate,
  minutesToDisplay,
  buildRequest,
};
